import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import api from "@/lib/api";
import { personName } from "@/lib/format";
import { VoiceSearchButton, appendSearch } from "@/components/VoiceSearchButton";
import { Users, FolderKanban, CheckSquare, Building2, FileText } from "lucide-react";

export const GlobalSearch = ({ open, setOpen }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ persone: [], pratiche: [], attivita: [], organizzazioni: [], documenti: [] });
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !/(INPUT|TEXTAREA)/.test(document.activeElement?.tagName)) {
        e.preventDefault(); setOpen(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setOpen(true); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (!q || q.length < 2) { setRes({ persone: [], pratiche: [], attivita: [], organizzazioni: [], documenti: [] }); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes(data); } catch (e) { console.error("Errore ricerca globale", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = useCallback((path) => { setOpen(false); setQ(""); navigate(path); }, [navigate, setOpen]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <div className="relative">
        <CommandInput placeholder="Cerca persone, pratiche, attività…" value={q} onValueChange={setQ} className="pr-11" data-testid="global-search-input" />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
          <VoiceSearchButton onResult={(t) => setQ((prev) => appendSearch(prev, t))} data-testid="global-search-mic" />
        </div>
      </div>
      <CommandList>
        <CommandEmpty>{q.length < 2 ? "Digita almeno 2 caratteri…" : "Nessun risultato"}</CommandEmpty>
        {res.persone?.length > 0 && (
          <CommandGroup heading="Persone">
            {res.persone.map((p) => (
              <CommandItem key={p.id} onSelect={() => go(`/app/persone/${p.id}`)}>
                <Users className="mr-2 h-4 w-4" />{personName(p)} <span className="ml-2 text-xs text-muted-foreground">{p.telefono_principale}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.pratiche?.length > 0 && (
          <CommandGroup heading="Pratiche">
            {res.pratiche.map((c) => (
              <CommandItem key={c.id} onSelect={() => go(`/app/pratiche/${c.id}`)}>
                <FolderKanban className="mr-2 h-4 w-4" />{c.numero} <span className="ml-2 text-xs text-muted-foreground truncate">{c.servizio_nome}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.attivita?.length > 0 && (
          <CommandGroup heading="Attività">
            {res.attivita.map((t) => (
              <CommandItem key={t.id} onSelect={() => go(`/app/attivita`)}>
                <CheckSquare className="mr-2 h-4 w-4" />{t.titolo}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.organizzazioni?.length > 0 && (
          <CommandGroup heading="Organizzazioni">
            {res.organizzazioni.map((o) => (
              <CommandItem key={o.id} onSelect={() => go(`/app/organizzazioni`)}>
                <Building2 className="mr-2 h-4 w-4" />{o.denominazione}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.documenti?.length > 0 && (
          <CommandGroup heading="Documenti">
            {res.documenti.map((d) => (
              <CommandItem key={d.id} onSelect={() => go(`/app/documenti`)}>
                <FileText className="mr-2 h-4 w-4" />{d.titolo}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};
